import { ReactNode, useMemo } from 'react';

import { Auth0Provider, useAuth0, RedirectLoginOptions, LogoutOptions, Auth0ProviderOptions } from '@auth0/auth0-react';
import jwt_decode from 'jwt-decode';

import { AuthContext, AuthContextState, DecodedJWT } from '@arrived/auth';
import { ROUTES } from '@arrived/common';
import { Loading } from '@arrived/components';
import { CONFIG } from '@arrived/config';

interface ArrivedWebAuthProviderProps {
  allowUnauthorized?: boolean;
  redirectOverrides?: {
    login?: string;
    signup?: string;
    logout?: string;
  };
  children?: ReactNode;
}

export const AuthContextProvider = ({
  children,
  allowUnauthorized,
  redirectOverrides,
  ...auth0Overrides
}: Partial<Auth0ProviderOptions> & ArrivedWebAuthProviderProps) => {
  const redirectUri = useMemo(
    () =>
      (typeof window === 'undefined' || window.location.pathname === CONFIG.baseHref
        ? CONFIG.appRoot
        : window.location.origin + CONFIG.baseHref) + ROUTES.authCallback,
    []
  );

  return (
    <Auth0Provider
      domain={CONFIG.auth0.domain}
      clientId={CONFIG.auth0.clientId}
      audience={CONFIG.auth0.audience}
      scope={CONFIG.auth0.scope}
      redirectUri={redirectUri}
      useRefreshTokens
      {...auth0Overrides}
    >
      <ArrivedWebAuthProvider allowUnauthorized={allowUnauthorized} redirectOverrides={redirectOverrides}>
        {children}
      </ArrivedWebAuthProvider>
    </Auth0Provider>
  );
};

const ArrivedWebAuthProvider = ({ children, allowUnauthorized, redirectOverrides }: ArrivedWebAuthProviderProps) => {
  const { isAuthenticated, isLoading, getIdTokenClaims, getAccessTokenSilently, loginWithRedirect, logout, user } =
    useAuth0();

  const auth = useMemo<AuthContextState>(() => {
    return {
      login: (passedFields?: RedirectLoginOptions) => {
        loginWithRedirect({
          appState: { returnTo: redirectOverrides?.login ?? window.location.origin + CONFIG.baseHref },
          prompt: 'login',
          ...passedFields,
        });
      },
      signup: () =>
        loginWithRedirect({
          screen_hint: 'signup',
          appState: { returnTo: redirectOverrides?.signup ?? window.location.origin + CONFIG.baseHref },
        }),
      getIdToken: async () => {
        // If this exists in local storage, it will be instant
        const idTokenClaims = await getIdTokenClaims();
        return idTokenClaims?.__raw;
      },
      getAccessToken: async () => {
        return await getAccessTokenSilently();
      },
      logout: async (passedFileds?: LogoutOptions) => {
        logout({
          returnTo: redirectOverrides?.logout ?? window.location.origin + CONFIG.baseHref + ROUTES.logOut,
          ...passedFileds,
        });
      },
      getPermissions: async () => {
        const accessToken = await getAccessTokenSilently();
        const decoded = jwt_decode<DecodedJWT>(accessToken);
        return decoded.permissions;
      },
      user,
      isAuthenticated,
      isLoading,
    };
  }, [
    isAuthenticated,
    isLoading,
    getIdTokenClaims,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
    user,
    redirectOverrides,
  ]);

  if (allowUnauthorized) return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
  return !isLoading ? <AuthContext.Provider value={auth}>{children}</AuthContext.Provider> : <Loading />;
};
